<script setup lang="ts">
import SimpleBar from "simplebar";
import { computed, onMounted, ref, watch, watchEffect } from "vue";
import {
  DateMark,
  NewMessagesMark,
  messagesList,
} from "../../states/chatState";
import { locale } from "../../locale";
import TextMessage from "./message_companents/TextMessage.vue";
import FileMessage from "./message_companents/FileMessage.vue";
import {
  resetScrollInstance,
  setScrollChat,
} from "../../states/scrollChatState";
import { MessageItem } from "../../types/localMessage";
import { readMarkEl, setReadMark } from "../../states/readMarkState";
import NewMessagesSeparator from "./NewMessagesSeparator.vue";
import DateSeparator from "./DateSeparator.vue";
import ScrollToBottomBtn from "./ScrollToBottomBtn.vue";
import { viewState } from "../../states/viewState";

const scroll = ref<HTMLElement | null>(null);

onMounted(() => {
  messageArea.value?.addEventListener("click", () => {
    if (window.getSelection && window.getSelection()?.type === "Range") return;
    else messageArea.value?.focus();
  });
  messageArea.value?.addEventListener("focus", () => {
    viewState.chatFocused = true;
  });
  messageArea.value?.addEventListener("blur", () => {
    viewState.chatFocused = false;
  });
});

const emit = defineEmits(["scrollready"]);

watchEffect(() => {
  if (scroll.value) {
    const smp = new SimpleBar(scroll.value, { autoHide: false });
    setScrollChat(smp);
    setTimeout(() => {
      emit("scrollready");
    }, 300);
  } else {
    resetScrollInstance();
  }
});
function getMessageKey(m: (typeof messagesList.value)[number]) {
  if ("local" in m) {
    if (m.local && m.local.id) return m.local.id;
    else throw "local id missing";
  } else if ("id" in m) {
    if (m.id) return m.id;
    else throw "id missing";
  } else if ("text" in m) {
    return m.text;
  } else {
    return m.type;
  }
}
let observer: IntersectionObserver;
const messageArea = ref<HTMLElement | null>(null);
watch(messageArea, (next) => {
  if (next) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
          if (entry.isIntersecting) {
            const target = entry.target as MessageRef;
            setReadMark(target.messageInfo);
          }
        });
      },
      {
        root: messageArea.value,
        threshold: 0.01,
      },
    );
    stash.forEach((el) => observer.observe(el));
  }
});
const stash: HTMLElement[] = [];

const messageRefs = (
  el: MessageRef,
  message: MessageItem | NewMessagesMark | DateMark,
) => {
  if (message.type === "NewMessagesMark") {
    if (el) readMarkEl.value = el;
    else readMarkEl.value = null;
    return;
  }
  if (!el || message.type === "DateMark") return;
  el.messageInfo = {
    remoteId: message.id,
    localId: message.local?.id,
  };
  if (observer) {
    observer.observe(el as HTMLElement);
  } else {
    stash.push(el as HTMLElement);
  }
};
interface MessageRef extends HTMLElement {
  messageInfo: {
    remoteId?: string;
    localId?: string;
  };
}
const byDate = computed(() => {
  const res = [] as (typeof messagesList.value)[];
  let last: typeof messagesList.value = [];
  for (const message of messagesList.value) {
    if (message.type === "DateMark") {
      last = [message];
      res.push(last);
    } else {
      last.push(message);
    }
  }
  return res;
});
</script>
<template>
  <div ref="messageArea" tabindex="0" class="chat-messages-wrapper">
    <ScrollToBottomBtn />
    <div v-if="messagesList.length" ref="scroll" class="scroll-chat">
      <div class="chat-messages-area">
        <div class="day" v-for="(day, id) in byDate" :key="id">
          <div
            class="one-message-wrapper"
            :class="{
              'date-sticky': message.type === 'DateMark',
            }"
            v-for="message in day"
            :key="getMessageKey(message)"
          >
            <div
              class="one-message-wrapper"
              :ref="(el) => messageRefs(el as MessageRef, message)"
            >
              <TextMessage v-if="message.type === 'Text'" :message="message" />
              <FileMessage
                v-else-if="message.type === 'File'"
                :message="message"
              />
              <NewMessagesSeparator
                v-else-if="message.type === 'NewMessagesMark'"
              />
              <DateSeparator
                v-else-if="message.type === 'DateMark'"
                :text="message.text"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="placeholder-chat">
      <div class="text">
        {{ locale.chat.emptyMessages }}
      </div>
    </div>
  </div>
</template>
<style scoped>
.one-message-wrapper {
  width: 100%;
}

.date-sticky {
  position: sticky;
  z-index: 2000;
  top: 8px;
}

.chat-messages-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  background-image: url("@images/chat_bg3.png");
  background-repeat: repeat;
}

.chat-messages-wrapper:focus {
  outline: none;
  border: none;
}

.scroll-chat {
  width: 100%;
  max-height: 100%;
  height: 100%;
}

.chat-messages-area {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding-top: 8px;
  padding-bottom: 8px;
}

.day {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
}

.placeholder-chat {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.placeholder-chat .text {
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 400;
  line-height: 17.7px;
  text-align: center;
  color: rgba(164, 164, 164, 1);
}
</style>
<style>
.chat-messages-wrapper .simplebar-vertical .simplebar-scrollbar::before {
  background-color: #676666;
  width: 4px;
}
</style>
